import React from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
} from '@mui/material';
import {
  AccountCircle,
  Dashboard,
  Engineering,
  Assessment,
  Psychology,
  PrecisionManufacturing,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { User } from '../types';

interface NavbarProps {
  user: User;
  onLogout: () => void;
}

const Navbar: React.FC<NavbarProps> = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleClose();
    onLogout();
  };

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  return (
    <AppBar 
      position="static"
      sx={{
        bgcolor: 'rgba(0, 0, 0, 0.9)',
        backdropFilter: 'blur(20px)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        boxShadow: 'none',
      }}
    >
      <Toolbar>
        <Typography 
          variant="h6" 
          component="div" 
          sx={{ 
            flexGrow: 1,
            fontWeight: 200,
            letterSpacing: '-0.02em',
            background: 'linear-gradient(135deg, #00D4FF, #34C759)',
            backgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            fontSize: '1.5rem',
          }}
        >
          DIGITAL TWIN 4.0
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            color="inherit"
            startIcon={<Dashboard />}
            onClick={() => navigate('/dashboard')}
            sx={{ 
              color: '#ffffff',
              fontWeight: 300,
              letterSpacing: '0.02em',
              px: 3,
              py: 1,
              borderRadius: '25px',
              backgroundColor: isActive('/dashboard') ? 'rgba(0, 212, 255, 0.2)' : 'transparent',
              border: isActive('/dashboard') ? '1px solid #00D4FF' : '1px solid transparent',
              '&:hover': {
                backgroundColor: 'rgba(0, 212, 255, 0.1)',
                border: '1px solid #00D4FF',
                boxShadow: '0 0 20px rgba(0, 212, 255, 0.3)',
              },
              transition: 'all 0.3s ease',
            }}
          >
            Dashboard
          </Button>

          <Button
            color="inherit"
            startIcon={<Engineering />}
            onClick={() => navigate('/assets')}
            sx={{ 
              color: '#ffffff',
              fontWeight: 300,
              letterSpacing: '0.02em',
              px: 3,
              py: 1,
              borderRadius: '25px',
              backgroundColor: isActive('/assets') ? 'rgba(0, 212, 255, 0.2)' : 'transparent',
              border: isActive('/assets') ? '1px solid #00D4FF' : '1px solid transparent',
              '&:hover': {
                backgroundColor: 'rgba(0, 212, 255, 0.1)',
                border: '1px solid #00D4FF',
                boxShadow: '0 0 20px rgba(0, 212, 255, 0.3)',
              },
              transition: 'all 0.3s ease',
            }}
          >
            Assets
          </Button>

          <Button
            color="inherit"
            startIcon={<Assessment />}
            onClick={() => navigate('/reports')}
            sx={{ 
              color: '#ffffff',
              fontWeight: 300,
              letterSpacing: '0.02em',
              px: 3,
              py: 1,
              borderRadius: '25px',
              backgroundColor: isActive('/reports') ? 'rgba(0, 212, 255, 0.2)' : 'transparent',
              border: isActive('/reports') ? '1px solid #00D4FF' : '1px solid transparent',
              '&:hover': {
                backgroundColor: 'rgba(0, 212, 255, 0.1)',
                border: '1px solid #00D4FF',
                boxShadow: '0 0 20px rgba(0, 212, 255, 0.3)',
              },
              transition: 'all 0.3s ease',
            }}
          >
            Reports
          </Button>

          <Button
            color="inherit"
            startIcon={<Psychology />}
            onClick={() => navigate('/ai-models')}
            sx={{ 
              color: '#ffffff',
              fontWeight: 300,
              letterSpacing: '0.02em',
              px: 3,
              py: 1,
              borderRadius: '25px',
              backgroundColor: isActive('/ai-models') ? 'rgba(0, 212, 255, 0.2)' : 'transparent',
              border: isActive('/ai-models') ? '1px solid #00D4FF' : '1px solid transparent',
              '&:hover': {
                backgroundColor: 'rgba(0, 212, 255, 0.1)',
                border: '1px solid #00D4FF',
                boxShadow: '0 0 20px rgba(0, 212, 255, 0.3)',
              },
              transition: 'all 0.3s ease',
            }}
          >
            AI Models
          </Button>

          <IconButton
            size="large"
            aria-label="account of current user"
            aria-controls="menu-appbar"
            aria-haspopup="true"
            onClick={handleMenu}
            color="inherit"
          >
            <Avatar sx={{ width: 32, height: 32 }}>
              {user.firstName?.[0] || user.username[0].toUpperCase()}
            </Avatar>
          </IconButton>

          <Menu
            id="menu-appbar"
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(anchorEl)}
            onClose={handleClose}
          >
            <MenuItem disabled>
              <Typography variant="body2" color="text.secondary">
                {user.firstName && user.lastName 
                  ? `${user.firstName} ${user.lastName}` 
                  : user.username}
              </Typography>
            </MenuItem>
            <MenuItem disabled>
              <Typography variant="caption" color="text.secondary">
                {user.email}
              </Typography>
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              Logout
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;





